import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogWithNotes } from './GlobalComponents/confirm-dialog-with-notes/confirm-dialog-notes';
import { customDialogResponse, DialogDataInterface } from './GlobalComponents/confirm-dialog-with-notes/dialog-data.interface';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environments';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FuseLoadingService } from '@fuse/services/loading';


type FileExtension = 'csv' | 'pdf' | 'xls' | 'xlsx' | 'doc' | 'docx' | 'zip';

@Injectable({providedIn: 'root'})
export class UtilsService {

    private _activeEntityUuid: BehaviorSubject<string> = new BehaviorSubject(null)
    private _seccion_uuid: BehaviorSubject<number> = new BehaviorSubject(0)
    private _esRutaGenerales = new BehaviorSubject<boolean>(false);
    
    constructor(
        private dialog: MatDialog,
        private confirmDialog: FuseConfirmationService,
        private sckaBar: MatSnackBar,
        private http: HttpClient,
        private sanitizer: DomSanitizer,
        private _loadingBar: FuseLoadingService,
        
    ) { }


    set activeEntityUuid(entity_uuid: string) {
        this._activeEntityUuid.next(entity_uuid)
    }

    get activeEntityUuid(){
        return this._activeEntityUuid.getValue()
    }

    set seccion_uuid(seccion_id:number){
        this._seccion_uuid.next(seccion_id)
    }

    get seccion_uuid_value(): number{
        return  this._seccion_uuid.getValue();
    }

     // Setter y getter para esRutaGeneral
    set esRutaGenerales(value: boolean) {
        this._esRutaGenerales.next(value);
    }

    get esRutaGenerales_value(): boolean {
        return this._esRutaGenerales.getValue();
    }
    


    openConfirDialogWithNotes(dialogConfig: DialogDataInterface): Promise<customDialogResponse> {
        const modal = this.dialog.open(ConfirmDialogWithNotes, {
            data: dialogConfig,
            disableClose: true,
            width: dialogConfig.width ??  '40%'
        })

        return new Promise((resolve, reject ) => {
            modal.afterClosed()
                .subscribe((response: customDialogResponse) => {
                    resolve(response)
                })
        })
       

    }

    open_confirm_dialog(message: string, _title?: string): Observable<any> {
        let title  = (_title) ? _title : 'Confirmar Acción';

        const dialog =this.confirmDialog.open(
            {
                title,
                message,
                dismissible: true,
                icon: {
                    show: true,
                    name: "heroicons_outline:exclamation-triangle",
                    color: "info"
                },
                actions: {
                    confirm: {
                        show: true,
                        label: "Confirmar"
                    },
                    cancel: {
                        show:true,
                        label: 'Cancelar'
                    }
                }
            }
        )

        return dialog.afterClosed()
    }

    openSuccessSnakBar(message?:string) {
        return this.sckaBar.open(
            message,
            '',
            {
                duration: 8000,
                verticalPosition: 'top',
                panelClass: ['bg-green-600','text-white']
            }
        );
    }

    showErrorMessage(message?:string){

        return  this.confirmDialog.open( {
            title: 'Ups!',
            message: message,
            icon: {
                show: true,
                name: 'error',
                color:'warning'
            },
            actions: {
                confirm: { show: false},
                cancel: {show:  true}
            },
            dismissible:true

        })

    }

    getVADocumentPreview(documentId: number): Promise<SafeResourceUrl>{
      const url = `${environment.API_SERVER_URL}/api/archivos/visualizar/${documentId}`

      return new Promise((resolve, reject) => {
            this.http.get(url, {responseType: 'blob'})
                .subscribe(responseBlob => {
                    if (responseBlob){
                        const blobUrl = URL.createObjectURL(responseBlob);
                        const documenSanitizedURL = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
                        resolve(documenSanitizedURL)
                    }
    
                    resolve (null) 
                })
        })            
    }


    getDocumentoBlob(documentId: number): Promise<Blob> {
        const url = `${environment.API_SERVER_URL}/api/archivos/visualizar/${documentId}`;
    
        return new Promise((resolve, reject) => {
            this.http.get(url, { responseType: 'blob' })
                .subscribe((responseBlob) => {
                    resolve(responseBlob);
                },error => {
                    reject(error);
                }
            );
        });
    }

    getArchivosInternos(documentId: number): Promise<SafeResourceUrl>{
        const url = `${environment.API_SERVER_URL}/api/archivos/obtener_archivo/${documentId}`
    
        return new Promise((resolve, reject) => {
            this.http.get(url, {responseType: 'blob'})
                .subscribe(responseBlob => {
                    if (responseBlob){
                        const blobUrl = URL.createObjectURL(responseBlob);
                        const documenSanitizedURL = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
                        resolve(documenSanitizedURL)
                    } 
                },error => {
                    reject(error);
                })
        })            
    }   

    getArchivosInternosBlob(documentId: number): Promise<Blob>{
        const url = `${environment.API_SERVER_URL}/api/archivos/obtener_archivo/${documentId}`
    
        return new Promise((resolve, reject) => {
            this.http.get(url, {responseType: 'blob'})
                .subscribe(
                    blob => resolve(blob),     
                error => reject(error)
                )
        })            
    } 
    

    allowedExtensions(extension: string) {
        const allowedExtensions = ['pdf','jpg','png','jpeg','bmp']
        return allowedExtensions.includes(extension)

    }

    downloadFile(blob: Blob, fileName?: string, _type?: string): void {
        // Validación de tipo MIME real del Blob
        const mimeToExtension: { [key: string]: string } = {
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
            'application/pdf': 'pdf',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
        };

        const actualExtension = mimeToExtension[blob.type] || _type || 'bin';

        // Valida que el nombre del archivo tenga la extensión correcta
        let finalFileName = fileName || 'archivo';
        if (!finalFileName.endsWith(`.${actualExtension}`)) {
            finalFileName += `.${actualExtension}`;
        }

        // Crear URL y descargar
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = finalFileName;
        a.click();
        URL.revokeObjectURL(url);
    }

    exportFile(endPoint: string,  params?: any, type?: FileExtension, fileName?: string): Promise<void> {
        const url = `${environment.API_SERVER_URL}${endPoint}`;

        this._loadingBar.show();
        return new Promise(async (resolve, reject) => {
            this.http.get(url, { params, responseType: 'blob' }).subscribe(
                async (data: Blob) => {
                    this._loadingBar.hide();
                    try {
                        if (data) {
                            const text = await data.text();
                            try {
                                const json = JSON.parse(text);
                                if (json?.isSuccess === false) {
                                    this.showErrorMessage(json.message || 'Ha ocurrido un error al generar el archivo.');
                                    return reject(new Error(json.message || 'Operación no exitosa'));
                                }
                            } catch {}
                        }

                        this.downloadFileReport(data, fileName, type);
                        resolve();
                    } catch (e) {
                        reject(e);
                    }
                },
                async (error) => {
                    this._loadingBar.hide();
                    try {
                        const raw = await error?.error?.text?.();
                        let message = '';
                        try {
                            const parsed = JSON.parse(raw);
                            message = parsed?.message || parsed?.msg || '';
                        } catch {}
                        this.showErrorMessage(message || 'Ha ocurrido un error al generar el archivo.');
                    } catch {
                        this.showErrorMessage('Ha ocurrido un error al generar el archivo.');
                    }
                    reject(error);
                }
            );
        });
    }

    downloadFileReport(data: any, fileName?: string, _type?: FileExtension): void {
        const type =  (_type) ? _type : 'docx';
        const mime = this.getMimeType(type);
        const blob = new Blob([data], { type: mime });
        const url =  window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        const datetime = (new Date()).toJSON();

        anchor.download = `${fileName}-${datetime}.${type}`;
        anchor.href = url;
        anchor.click();
    }

    private getMimeType (typeName: FileExtension): string {
        let type: string;

        switch(typeName) {
            case 'csv': type = 'text/csv'; break;
            case 'doc': type = 'application/msword'; break;
            case 'docx': type = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'; break;
            case 'xls': type = 'application/vnd.ms-excel'; break;
            case 'xlsx': type = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'; break;
            case 'zip': type = 'application/zip'; break;
            default: type = 'application/pdf'; break;
        }

        return type;
    }

}
